import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Users, 
  Video, 
  VideoOff, 
  Mic, 
  MicOff, 
  ChevronLeft,
  Crown,
  ChevronUp,
  Volume2,
  Camera,
  CheckCircle,
  X,
  Settings
} from 'lucide-react';
import DateTimeHeader from '../components/DateTimeHeader';
import HeaderActions from '../components/HeaderActions';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

const WaitingRoom: React.FC = () => {
  // const [darkMode, setDarkMode] = useState(false);
  const {darkMode, toggleDarkMode} = useTheme();
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [microphoneEnabled, setMicrophoneEnabled] = useState(false);
  const [userName, setUserName] = useState('');
  const [meetingName, setMeetingName] = useState('Team Meeting');
  const [meetingHost, setMeetingHost] = useState('John Smith');
  const [waitingTime, setWaitingTime] = useState(0);
  const profilePhotoUrl = localStorage.getItem('profilePhotoUrl') || 'https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png';
  const [admissionStatus, setAdmissionStatus] = useState<'waiting' | 'admitted' | 'denied'>('waiting');
  const [showMicDropdown, setShowMicDropdown] = useState(false);
  const [showCameraDropdown, setShowCameraDropdown] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsTab, setSettingsTab] = useState<'Audio' | 'Video' | 'Notifications' | 'Profile' | 'Shortcuts' | 'General'>('Audio');
  const [selectedMicrophone, setSelectedMicrophone] = useState('Default Microphone');
  const [selectedSpeaker, setSelectedSpeaker] = useState('Default Speaker');
  const [selectedCamera, setSelectedCamera] = useState('Default Camera');
  const [mirrorVideo, setMirrorVideo] = useState(false);
  const [enableNoiseSuppression, setEnableNoiseSuppression] = useState(true);
  const {user, logout} = useAuth();

  const mockAudioDevices = {
    microphones: [
      { id: 'mic1', name: 'Default Microphone', isActive: true },
      { id: 'mic2', name: 'USB Microphone', isActive: false },
      { id: 'mic3', name: 'Built-in Microphone', isActive: false },
      { id: 'mic4', name: 'Bluetooth Headset Mic', isActive: false }
    ],
    speakers: [
      { id: 'spk1', name: 'Default Speaker', isActive: true },
      { id: 'spk2', name: 'USB Headphones', isActive: false },
      { id: 'spk3', name: 'Built-in Speakers', isActive: false },
      { id: 'spk4', name: 'Bluetooth Headset', isActive: false }
    ],
    cameras: [
      { id: 'cam1', name: 'Default Camera', isActive: true },
      { id: 'cam2', name: 'HD USB Camera', isActive: false },
      { id: 'cam3', name: 'Built-in Camera', isActive: false },
      { id: 'cam4', name: 'Virtual Camera', isActive: false }
    ]
  };

  const handleMicrophoneSelect = (micName: string) => setSelectedMicrophone(micName);
  const handleSpeakerSelect = (speakerName: string) => setSelectedSpeaker(speakerName);
  const handleCameraSelect = (cameraName: string) => setSelectedCamera(cameraName);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Mock notifications for header consistency
  const notifications: any[] = [];

  // Mock waiting participants (for host view)
  const [waitingParticipants] = useState([
    { id: 1, name: 'Alice Johnson', avatar: 'https://randomuser.me/api/portraits/women/32.jpg', waitTime: '2:30' },
    { id: 2, name: 'Bob Smith', avatar: 'https://randomuser.me/api/portraits/men/45.jpg', waitTime: '1:15' },
    { id: 3, name: 'Carol Davis', avatar: 'https://randomuser.me/api/portraits/women/68.jpg', waitTime: '0:45' }
  ]);

  // Check if current user is host (in real app, this would come from auth context)
  const [isHost] = useState(false); // Set to true to test host view

  useEffect(() => {
    // Get user preferences from localStorage
    const savedUserName = localStorage.getItem('userName') || '';
    const savedCameraEnabled = localStorage.getItem('cameraEnabled') === 'true';
    const savedMicrophoneEnabled = localStorage.getItem('microphoneEnabled') === 'true';
    
    setUserName(savedUserName);
    setCameraEnabled(savedCameraEnabled);
    setMicrophoneEnabled(savedMicrophoneEnabled);

    // Get meeting details from URL parameters
    const meetingNameParam = searchParams.get('meetingName');
    if (meetingNameParam) {
      setMeetingName(decodeURIComponent(meetingNameParam));
    }

    const meetingHostParam = searchParams.get('meetingHost');
    if (meetingHostParam) {
      setMeetingHost(decodeURIComponent(meetingHostParam));
    }

    // Start waiting timer
    const interval = setInterval(() => {
      setWaitingTime(prev => prev + 1);
    }, 1000);

    // Simulate admission process (in real app, this would be WebSocket-based)
    const admissionTimer = setTimeout(() => {
      if (Math.random() > 0.3) {
        setAdmissionStatus('admitted');
        setTimeout(() => {
          navigate('/meeting-new');
        }, 2000);
      }
    }, 8000); // Simulate 8 seconds wait

    return () => {
      clearInterval(interval);
      clearTimeout(admissionTimer);
    };
  }, [searchParams, navigate]);

  const formatWaitingTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleGoBack = () => {
    navigate('/pre-meeting');
  };

  const handleAdmitParticipant = (participantId: number) => {
    // In real app, this would send a WebSocket message to admit the participant
    console.log(`Admitting participant ${participantId}`);
  };

  const handleDenyParticipant = (participantId: number) => {
    // In real app, this would send a WebSocket message to deny the participant
    console.log(`Denying participant ${participantId}`);
  };

  const renderWaitingView = () => (
    <div className="w-full h-full flex flex-col lg:flex-row justify-center lg:items-center">
      {/* Left Side - Meeting Information (40%) */}
      <div className="w-full lg:w-2/5 lg:pr-6 mb-4 lg:mb-0 flex flex-col">
        <div className={`p-4 sm:p-6 rounded-2xl h-[420px] flex flex-col justify-center ${darkMode ? 'bg-white/5 backdrop-blur-xl border border-white/10' : 'bg-white/80 backdrop-blur-xl border border-gray-200/50'} shadow-xl`}>
          {/* Status Message */}
          <div className="text-center mb-4">
            <h2 className={`text-xl sm:text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {admissionStatus === 'waiting' && (
                <span className="inline-flex items-center gap-2">
                  <span className="relative inline-flex items-center justify-center w-5 h-5">
                    <span className="absolute inset-0 rounded-full border-2 border-transparent border-t-pink-500 border-r-red-500 animate-spin"></span>
                    <span className={`w-3 h-3 rounded-full ${darkMode ? 'bg-[#1a1a2e]' : 'bg-white'}`}></span>
                  </span>
                  <span>Waiting for admission</span>
                </span>
              )}
              {admissionStatus === 'admitted' && 'Admitted to meeting!'}
              {admissionStatus === 'denied' && 'Access denied'}
            </h2>
            <p className={`text-base sm:text-lg ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>
              {admissionStatus === 'waiting' && meetingName}
              {admissionStatus === 'admitted' && 'Redirecting you to the meeting...'}
              {admissionStatus === 'denied' && 'The host has denied your request to join'}
            </p>
            {admissionStatus === 'waiting' && (
              <p className={`${darkMode ? 'text-white/60' : 'text-gray-500'} text-sm mt-1`}>
                {formatWaitingTime(waitingTime)}
              </p>
            )}
          </div>



          {/* Action Button */}
          {admissionStatus === 'waiting' && (
            <div className="text-center">
              <button
                onClick={handleGoBack}
                className={`w-full py-3 px-6 rounded-xl font-medium text-sm transition-all duration-200 hover:bg-gradient-to-r hover:from-pink-500 hover:to-red-500 hover:text-white ${
                  darkMode 
                    ? 'bg-white/10 text-white' 
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                Leave Waiting Room
              </button>
            </div>
          )}

          {admissionStatus === 'denied' && (
            <button
              onClick={handleGoBack}
              className="w-full py-3 px-6 rounded-xl font-medium text-sm bg-gradient-to-r from-red-500 to-pink-500 text-white hover:from-red-600 hover:to-pink-600 transition-all duration-200"
            >
              Go Back
            </button>
          )}
        </div>
      </div>

      {/* Right Side - Camera Screen and Controls (60%) */}
      <div className="w-full lg:w-7/12 lg:pl-6 flex flex-col">
        <div className={`p-4 sm:p-6 rounded-2xl h-[420px] flex flex-col justify-center ${darkMode ? 'bg-white/5 backdrop-blur-xl border border-white/10' : 'bg-white/80 backdrop-blur-xl border border-gray-200/50'} shadow-xl`}>
          {/* Video Preview */}
          <div className={`rounded-xl overflow-hidden mb-4 ${darkMode ? 'bg-gray-800' : 'bg-gray-200'} aspect-video flex items-center justify-center w-11/12 sm:w-3/4 lg:w-3/5 mx-auto relative`}>
            {cameraEnabled ? (
              <div className="w-full h-full flex items-center justify-center"></div>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="p-1 rounded-full bg-gradient-to-r from-pink-500 to-red-500">
                  <img src={profilePhotoUrl} alt="profile" className="w-24 h-24 rounded-full object-cover" />
                </div>
              </div>
            )}
            <div className="absolute bottom-3 left-3 z-30 pointer-events-none">
              <div className={`${darkMode ? 'text-white' : 'text-gray-900'} text-sm font-semibold`}>{userName || 'You'}</div>
              <div className={`text-[11px] ${darkMode ? 'text-white/80' : 'text-gray-800'}`}>{isHost ? 'Host' : 'Participant'}</div>
            </div>
          </div>

          {/* Media Controls */}
          <div className="flex gap-4 justify-center">
            {/* Microphone Button with Chevron */}
            <div className="relative">
              <button
                onClick={() => setMicrophoneEnabled(!microphoneEnabled)}
                className={`p-4 rounded-full transition-all duration-200 relative group ${
                  !microphoneEnabled
                    ? 'bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-xl'
                    : (darkMode ? 'bg-white/10 text-white hover:bg-white/20 hover:shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-lg')
                }`}
                aria-label={microphoneEnabled ? 'Mute microphone' : 'Unmute microphone'}
                title={microphoneEnabled ? 'Mute microphone' : 'Unmute microphone'}
              >
                {microphoneEnabled ? <Mic size={20} /> : <MicOff size={20} />}
                <button
                  onClick={(e) => { e.stopPropagation(); setShowMicDropdown(!showMicDropdown); }}
                  className={`absolute -top-1 -right-1 p-1.5 rounded-full transition-all duration-200 ${darkMode ? 'bg-gray-800/80 text-white hover:bg-gray-700/80' : 'bg-white/90 text-gray-600 hover:bg-gray-100/90'} shadow-sm`}
                  aria-label="Microphone options"
                  title="Microphone options"
                >
                  <ChevronUp size={12} className={`transition-transform duration-200 ${showMicDropdown ? 'rotate-180' : ''}`} />
                </button>
              </button>

              {/* Microphone Dropdown */}
              {showMicDropdown && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 w-56 rounded-xl shadow-2xl backdrop-blur-xl z-[9999]">
                  <div className={`${darkMode ? 'bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 border border-white/10' : 'bg-gradient-to-br from-white/95 via-gray-50/95 to-white/95 border border-gray-200/50'} rounded-xl px-2 py-3 shadow-xl`}>
                    {/* Microphone Section */}
                    <div className="mb-2 pt-1">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <div className={`${darkMode ? 'bg-white/5' : 'bg-gray-100'} p-0.5 rounded`}>
                          <Mic size={10} className={darkMode ? 'text-white/70' : 'text-gray-600'} />
                        </div>
                        <h4 className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-white/50' : 'text-gray-400'}`}>Microphone</h4>
                      </div>
                      <div className="space-y-0.5">
                        {mockAudioDevices.microphones.map((mic) => (
                          <button
                            key={mic.id}
                            onClick={(e) => { e.stopPropagation(); handleMicrophoneSelect(mic.name); }}
                            onMouseDown={(e) => e.preventDefault()}
                            tabIndex={-1}
                            type="button"
                            className={`w-full text-left p-1.5 rounded-lg transition-all duration-200 focus:outline-none ${
                              selectedMicrophone === mic.name
                                ? (darkMode ? 'bg-gradient-to-r from-pink-500 to-red-500 text-white' : 'bg-gradient-to-r from-pink-500 to-red-500 text-white')
                                : (darkMode ? 'text-white/80' : 'text-gray-700')
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium truncate">{mic.name}</span>
                              {selectedMicrophone === mic.name && (
                                <div className={darkMode ? 'bg-pink-500/20 p-0.5 rounded-full' : 'bg-pink-100 p-0.5 rounded-full'}>
                                  <CheckCircle size={10} className="text-white" />
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Speaker Section */}
                    <div>
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <div className={`${darkMode ? 'bg-white/5' : 'bg-gray-100'} p-0.5 rounded`}>
                          <Volume2 size={10} className={darkMode ? 'text-white/70' : 'text-gray-600'} />
                        </div>
                        <h4 className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-white/50' : 'text-gray-400'}`}>Speaker</h4>
                      </div>
                      <div className="space-y-0.5">
                        {mockAudioDevices.speakers.map((sp) => (
                          <button
                            key={sp.id}
                            onClick={(e) => { e.stopPropagation(); handleSpeakerSelect(sp.name); }}
                            onMouseDown={(e) => e.preventDefault()}
                            tabIndex={-1}
                            type="button"
                            className={`w-full text-left p-1.5 rounded-lg transition-all duration-200 focus:outline-none ${
                              selectedSpeaker === sp.name
                                ? (darkMode ? 'bg-gradient-to-r from-pink-500 to-red-500 text-white' : 'bg-gradient-to-r from-pink-500 to-red-500 text-white')
                                : (darkMode ? 'text-white/80' : 'text-gray-700')
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium truncate">{sp.name}</span>
                              {selectedSpeaker === sp.name && (
                                <div className={darkMode ? 'bg-pink-500/20 p-0.5 rounded-full' : 'bg-pink-100 p-0.5 rounded-full'}>
                                  <CheckCircle size={10} className="text-white" />
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Camera Button with Chevron */}
            <div className="relative">
              <button
                onClick={() => setCameraEnabled(!cameraEnabled)}
                className={`p-4 rounded-full transition-all duration-200 relative group ${
                  !cameraEnabled
                    ? 'bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-xl'
                    : (darkMode ? 'bg-white/10 text-white hover:bg-white/20 hover:shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-lg')
                }`}
                aria-label={cameraEnabled ? 'Turn off camera' : 'Turn on camera'}
                title={cameraEnabled ? 'Turn off camera' : 'Turn on camera'}
              >
                {cameraEnabled ? <Video size={20} /> : <VideoOff size={20} />}
                <button
                  onClick={(e) => { e.stopPropagation(); setShowCameraDropdown(!showCameraDropdown); }}
                  className={`absolute -top-1 -right-1 p-1.5 rounded-full transition-all duration-200 ${darkMode ? 'bg-gray-800/80 text-white hover:bg-gray-700/80' : 'bg-white/90 text-gray-600 hover:bg-gray-100/90'} shadow-sm`}
                  aria-label="Camera options"
                  title="Camera options"
                >
                  <ChevronUp size={12} className={`transition-transform duration-200 ${showCameraDropdown ? 'rotate-180' : ''}`} />
                </button>
              </button>

              {/* Camera Dropdown */}
              {showCameraDropdown && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 w-64 rounded-xl shadow-2xl backdrop-blur-xl z-[9999]">
                  <div className={`${darkMode ? 'bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 border border-white/10' : 'bg-gradient-to-br from-white/95 via-gray-50/95 to-white/95 border border-gray-200/50'} rounded-xl px-2 py-3 shadow-xl`}>
                    {/* Preview */}
                    <div className={`rounded-lg overflow-hidden mb-2 ${darkMode ? 'bg-gray-800' : 'bg-gray-200'} aspect-video flex items-center justify-center w-11/12 sm:w-3/4 lg:w-3/5 mx-auto`}>
                      <div className="w-full h-full flex items-center justify-center">
                        <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-300'} w-20 h-20 rounded-full flex items-center justify-center`}>
                          <Camera size={24} className={darkMode ? 'text-white/60' : 'text-gray-600'} />
                        </div>
                      </div>
                    </div>

                    {/* Camera List */}
                    <div className="space-y-0.5">
                      {mockAudioDevices.cameras.map((cam) => (
                        <button
                          key={cam.id}
                          onClick={(e) => { e.stopPropagation(); handleCameraSelect(cam.name); }}
                          onMouseDown={(e) => e.preventDefault()}
                          tabIndex={-1}
                          type="button"
                          className={`w-full text-left p-1.5 rounded-lg transition-all duration-200 focus:outline-none ${
                            selectedCamera === cam.name
                              ? (darkMode ? 'bg-gradient-to-r from-pink-500 to-red-500 text-white' : 'bg-gradient-to-r from-pink-500 to-red-500 text-white')
                              : (darkMode ? 'text-white/80' : 'text-gray-700')
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium truncate">{cam.name}</span>
                            {selectedCamera === cam.name && (
                              <div className={darkMode ? 'bg-pink-500/20 p-0.5 rounded-full' : 'bg-pink-100 p-0.5 rounded-full'}>
                                <CheckCircle size={10} className="text-white" />
                              </div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Mirror my video */}
                    <div className={`${darkMode ? 'border-white/10' : 'border-gray-200/70'} mt-4 pt-3 pb-2 border-t`}>
                      <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={mirrorVideo}
                          onChange={(e) => setMirrorVideo(e.target.checked)}
                          className="h-3.5 w-3.5 rounded border-gray-300 text-pink-500 focus:ring-0 focus:outline-none"
                        />
                        <span className={darkMode ? 'text-white/80' : 'text-gray-700'}>Mirror my video</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Settings Button opens modal */}
            <div className="relative">
              <button
                onClick={() => setShowSettingsModal(true)}
                className={`${darkMode ? 'bg-white/10 text-white hover:bg-white/20 hover:shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-lg'} p-4 rounded-full transition-all duration-200 group`}
                aria-label="Settings"
                title="Settings"
              >
                <Settings size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderHostView = () => (
    <div className="w-full max-w-4xl">
      {/* Host Controls */}
      <div className={`mb-8 p-6 rounded-2xl ${darkMode ? 'bg-white/5 backdrop-blur-xl border border-white/10' : 'bg-white/80 backdrop-blur-xl border border-gray-200/50'} shadow-xl`}>
        <div className="flex items-center gap-3 mb-6">
          <Crown className="text-yellow-500" size={24} />
          <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Host Controls</h2>
        </div>
        
        <div className={`p-4 rounded-xl ${darkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{meetingName}</h3>
              <p className={`text-sm ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>
                {waitingParticipants.length} participants waiting for admission
              </p>
            </div>
            <button
              onClick={() => navigate('/meeting-new')}
              className="py-2 px-4 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium hover:from-green-600 hover:to-emerald-600 transition-all duration-200"
            >
              Start Meeting
            </button>
          </div>
        </div>
      </div>

      {/* Waiting Participants */}
      <div className={`p-6 rounded-2xl ${darkMode ? 'bg-white/5 backdrop-blur-xl border border-white/10' : 'bg-white/80 backdrop-blur-xl border border-gray-200/50'} shadow-xl`}>
        <h3 className={`text-lg font-bold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Waiting Participants ({waitingParticipants.length})
        </h3>
        
        <div className="space-y-4">
          {waitingParticipants.map((participant) => (
            <div key={participant.id} className={`flex items-center justify-between p-4 rounded-xl ${darkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-3">
                <img 
                  src={participant.avatar} 
                  alt={participant.name}
                  className="w-10 h-10 rounded-full"
                />
                <div>
                  <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {participant.name}
                  </h4>
                  <p className={`text-sm ${darkMode ? 'text-white/70' : 'text-gray-600'}`}>
                    Waiting for {participant.waitTime}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => handleAdmitParticipant(participant.id)}
                  className="py-2 px-4 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium hover:from-green-600 hover:to-emerald-600 transition-all duration-200"
                >
                  Admit
                </button>
                <button
                  onClick={() => handleDenyParticipant(participant.id)}
                  className="py-2 px-4 rounded-lg bg-gradient-to-r from-red-500 to-pink-500 text-white font-medium hover:from-red-600 hover:to-pink-600 transition-all duration-200"
                >
                  Deny
                </button>
              </div>
            </div>
          ))}
        </div>
        
        {waitingParticipants.length === 0 && (
          <div className="text-center py-8">
            <Users className={`${darkMode ? 'text-white/40' : 'text-gray-400'}`} size={48} />
            <p className={`${darkMode ? 'text-white/70' : 'text-gray-600'}`}>No participants waiting</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-[#0a0a1a] via-[#1a1a2e] to-[#16213e] text-white' : 'bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 text-gray-900'}`}>
      {/* Top Status Bar */}
      <div className={`sticky top-0 z-50 ${darkMode ? 'bg-black/30' : 'bg-white/30'} backdrop-blur-xl border-b ${darkMode ? 'border-white/10' : 'border-gray-200/30'}`}>
        <div className="flex items-center justify-between px-4 sm:px-6 h-16">
          {/* Meeting Info */}
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="flex items-center gap-3">
              <button
                onClick={handleGoBack}
                className={`p-2.5 rounded-xl transition-all duration-200 group ${darkMode ? 'hover:bg-white/10 hover:shadow-lg' : 'hover:bg-gray-100 hover:shadow-md'}`}
              >
                <ChevronLeft className={`${darkMode ? 'text-white/70 group-hover:text-white' : 'text-gray-600 group-hover:text-gray-800'}`} size={20} />
              </button>
              <DateTimeHeader darkMode={darkMode} />
            </div>
          </div>

          {/* Status Indicators */}
          <HeaderActions darkMode={darkMode} setDarkMode={toggleDarkMode} />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex min-h-[calc(100vh-4rem)] items-center overflow-hidden">
        <div className="w-full px-4 py-2 sm:px-6 sm:py-3 lg:px-8 lg:py-4">
          {isHost ? renderHostView() : renderWaitingView()}
        </div>
      </div>

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowSettingsModal(false)} />
          <div className={`${darkMode ? 'bg-gradient-to-br from-[#12121d] to-[#1e1e2f] border border-white/10' : 'bg-white border border-gray-200/60'} relative w-full max-w-4xl rounded-2xl overflow-hidden`}>
            <button
              onClick={() => setShowSettingsModal(false)}
              className={`absolute top-3 right-3 p-2 rounded-lg ${darkMode ? 'hover:bg-white/10 text-white/70 hover:text-white' : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'}`}
              aria-label="Close"
              title="Close"
            >
              <X size={18} />
            </button>
            <div className="grid grid-cols-[220px_1fr] min-h-[520px]">
              <div className={`${darkMode ? 'bg-white/5 border-r border-white/10' : 'bg-gray-50 border-r border-gray-200'} p-4`}>
                {(['Audio','Video','Notifications','Profile','Shortcuts','General'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setSettingsTab(tab)}
                    className={`w-full text-left px-3 py-2 rounded-lg mb-2 text-sm font-medium transition-colors ${settingsTab === tab ? (darkMode ? 'bg-gradient-to-r from-pink-500 to-red-500 text-white' : 'bg-gradient-to-r from-pink-500 to-red-500 text-white') : (darkMode ? 'hover:bg-white/10 text-white/80' : 'hover:bg-gray-100 text-gray-800')}`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              <div className="p-6 space-y-4">
                {/* Audio section */}
                {settingsTab === 'Audio' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Audio</h3>
                      <p className={`${darkMode ? 'text-white/60' : 'text-gray-600'} text-sm`}>Configure microphone, speaker, and advanced audio options.</p>
                    </div>

                    {/* Microphone Section */}
                    <div className="space-y-4">
                      <div>
                        <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Microphone</h4>
                        <div className="space-y-3">
                          <div className="relative">
                            <select
                              value={selectedMicrophone}
                              onChange={(e) => setSelectedMicrophone(e.target.value)}
                              className={`w-full px-3 py-2 rounded-lg text-sm outline-none border transition-all duration-200 focus:ring-2 focus:ring-pink-500/50 focus:border-transparent ${darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50/80 border-gray-200 text-gray-900'}`}
                            >
                              {mockAudioDevices.microphones.map((mic) => (
                                <option key={mic.id} value={mic.name} className={darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}>
                                  {mic.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Live Audio Test Bar */}
                          <div className="space-y-2">
                            <div className={`${darkMode ? 'text-white/70' : 'text-gray-600'} text-xs`}>Live audio test</div>
                            <div className={`${darkMode ? 'bg-white/10' : 'bg-gray-200'} w-full h-2 rounded-full`}>
                              <div
                                className="h-full rounded-full bg-gradient-to-r from-pink-500 to-red-500 transition-all duration-300"
                                style={{ width: `${Math.random() * 60 + 20}%` }}
                              ></div>
                            </div>
                            <div className={`${darkMode ? 'text-white/50' : 'text-gray-500'} text-xs`}>Audio level: {Math.floor(Math.random() * 40 + 30)}%</div>
                          </div>
                        </div>
                      </div>

                      {/* Audio Output Section */}
                      <div>
                        <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Audio Output</h4>
                        <div className="space-y-3">
                          <div className="relative">
                            <select
                              value={selectedSpeaker}
                              onChange={(e) => setSelectedSpeaker(e.target.value)}
                              className={`w-full px-3 py-2 rounded-lg text-sm outline-none border transition-all duration-200 focus:ring-2 focus:ring-pink-500/50 focus:border-transparent ${darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50/80 border-gray-200 text-gray-900'}`}
                            >
                              {mockAudioDevices.speakers.map((speaker) => (
                                <option key={speaker.id} value={speaker.name} className={darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}>
                                  {speaker.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Test Button */}
                          <button
                            className={`${darkMode ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'} px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200`}
                          >
                            Test Audio Output
                          </button>
                        </div>
                      </div>

                      {/* Advanced */}
                      <div>
                        <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Advanced</h4>
                        <label className="flex items-center gap-3 text-sm cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={enableNoiseSuppression}
                            onChange={(e) => setEnableNoiseSuppression(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-pink-500 focus:ring-0 focus:outline-none"
                          />
                          <span className={darkMode ? 'text-white/90' : 'text-gray-900'}>Noise suppression</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
                {settingsTab === 'Video' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Video</h3>
                      <p className={`${darkMode ? 'text-white/60' : 'text-gray-600'} text-sm`}>Select camera, resolution, and layout preferences.</p>
                    </div>

                    {/* Camera Preview */}
                    <div>
                      <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Camera Preview</h4>
                      <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-200'} rounded-lg h-48 flex items-center justify-center`}>
                        <div className="w-full h-full flex items-center justify-center">
                          <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-300'} w-20 h-20 rounded-full flex items-center justify-center`}>
                            <Camera size={24} className={darkMode ? 'text-white/60' : 'text-gray-600'} />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Camera Settings */}
                    <div className="space-y-4">
                      <div>
                        <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Camera</h4>
                        <div className="space-y-3">
                          <div className="relative">
                            <select
                              value={selectedCamera}
                              onChange={(e) => setSelectedCamera(e.target.value)}
                              className={`w-full px-3 py-2 rounded-lg text-sm outline-none border transition-all duration-200 focus:ring-2 focus:ring-pink-500/50 focus:border-transparent ${darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50/80 border-gray-200 text-gray-900'}`}
                            >
                              {mockAudioDevices.cameras.map((cam) => (
                                <option key={cam.id} value={cam.name} className={darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}>
                                  {cam.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Mirror Video Checkbox */}
                          <label className="flex items-center gap-3 text-sm cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={mirrorVideo}
                              onChange={(e) => setMirrorVideo(e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-pink-500 focus:ring-0 focus:outline-none"
                            />
                            <span className={darkMode ? 'text-white/90' : 'text-gray-900'}>Mirror my video</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Notifications */}
                {settingsTab === 'Notifications' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Notifications</h3>
                      <p className={`${darkMode ? 'text-white/60' : 'text-gray-600'} text-sm`}>
                        Control system notifications and sounds.
                      </p>
                    </div>

                    {/* Play Sound On Section */}
                    <div>
                      <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Play sound on:</h4>
                      <div className="space-y-3">
                        {[
                          'Meeting Reactions',
                          'Incoming Message',
                          'Participant Joined',
                          'Participant Left',
                          'Talk While Muted',
                          'Participant Entered Lobby'
                        ].map((notification) => (
                          <label key={notification} className="flex items-center gap-3 text-sm cursor-pointer select-none">
                            <input
                              type="checkbox"
                              defaultChecked={true}
                              className="h-4 w-4 rounded border-gray-300 text-pink-500 focus:ring-0 focus:outline-none"
                            />
                            <span className={darkMode ? 'text-white/90' : 'text-gray-900'}>{notification}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {/* Profile */}
                {settingsTab === 'Profile' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Profile</h3>
                      <p className={`${darkMode ? 'text-white/60' : 'text-gray-600'} text-sm`}>
                        Update your profile details and preferences.
                      </p>
                    </div>

                    {/* Profile Image */}
                    <div>
                      <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Profile Image</h4>
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <img 
                            src="https://randomuser.me/api/portraits/women/44.jpg" 
                            alt="Profile" 
                            className="w-16 h-16 rounded-full border-2 border-gray-200"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Name */}
                    <div>
                      <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Name</h4>
                      <input
                        type="text"
                        defaultValue="Ledy Sintia"
                        className={`w-full px-3 py-2 rounded-lg text-sm outline-none border transition-all duration-200 focus:ring-2 focus:ring-pink-500/50 focus:border-transparent ${darkMode ? 'bg-white/5 border-white/10 text-white placeholder-white/40' : 'bg-gray-50/80 border-gray-200 text-gray-900 placeholder-gray-500'}`}
                        placeholder="Enter your name"
                      />
                    </div>

                    {/* Logged In As */}
                    <div>
                      <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Logged in as</h4>
                      <div className={`px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-white/5 text-white/70' : 'bg-gray-50/80 text-gray-600'}`}>
                        {user ? user.email : "guest@example.com"}
                      </div>
                    </div>
                  </div>
                )}
                {/* Shortcuts */}
                {settingsTab === 'Shortcuts' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Shortcuts</h3>
                      <p className={`${darkMode ? 'text-white/60' : 'text-gray-600'} text-sm`}>
                        Configure keyboard shortcuts for quick actions.
                      </p>
                    </div>

                    {/* Enable Keyboard Shortcuts */}
                    <div>
                      <label className="flex items-center gap-3 text-sm cursor-pointer select-none">
                        <input
                          type="checkbox"
                          defaultChecked={true}
                          className="h-4 w-4 rounded border-gray-300 text-pink-500 focus:ring-0 focus:outline-none"
                        />
                        <span className={darkMode ? 'text-white/90' : 'text-gray-900'}>Enable keyboard shortcuts</span>
                      </label>
                    </div>

                    {/* Shortcuts List */}
                    <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                      {[
                        { action: 'Show or hide video thumbnails', key: 'F' },
                        { action: 'Mute or unmute your microphone', key: 'M' },
                        { action: 'Start or stop camera', key: 'V' },
                        { action: 'Manage video quality', key: 'A' },
                        { action: 'Open or close the chat', key: 'C' },
                        { action: 'Switch between camera and screen sharing', key: 'D' },
                        { action: 'Show or hide the participants pane', key: 'P' },
                        { action: 'Raise or lower your hand', key: 'R' },
                        { action: 'View or exit full screen', key: 'S' },
                        { action: 'Toggle tile view', key: 'W' },
                        { action: 'Show participants stats', key: 'T' },
                        { action: 'Send thumbs up reaction', key: '⌥ + T' },
                        { action: 'Send clap reaction', key: '⌥ + C' },
                        { action: 'Send laugh reaction', key: '⌥ + L' },
                        { action: 'Send surprised reaction', key: '⌥ + O' },
                        { action: 'Send boo reaction', key: '⌥ + B' },
                        { action: 'Send silence reaction', key: '⌥ + S' },
                        { action: 'Show or hide keyboard shortcuts', key: '?' },
                        { action: 'Push to talk', key: 'Space' },
                        { action: 'Focus on your video', key: '0' },
                        { action: 'Focus on another person\'s video', key: '1-9' }
                      ].map((shortcut, index) => (
                        <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-white/5' : 'bg-gray-50/80'}`}>
                          <span className={`text-sm ${darkMode ? 'text-white/90' : 'text-gray-900'}`}>{shortcut.action}</span>
                          <div className={`px-2 py-1 rounded text-xs font-medium ${darkMode ? 'bg-white/10 text-white/80' : 'bg-gray-200 text-gray-700'}`}>
                            {shortcut.key}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* General */}
                {settingsTab === 'General' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">General</h3>
                      <p className={`${darkMode ? 'text-white/60' : 'text-gray-600'} text-sm`}>
                        General application settings.
                      </p>
                    </div>

                    {/* Enable Pre-meeting Screen */}
                    <div>
                      <label className="flex items-center gap-3 text-sm cursor-pointer select-none">
                        <input
                          type="checkbox"
                          defaultChecked={true}
                          className="h-4 w-4 rounded border-gray-300 text-pink-500 focus:ring-0 focus:outline-none"
                        />
                        <span className={darkMode ? 'text-white/90' : 'text-gray-900'}>Enable pre-meeting screen</span>
                      </label>
                    </div>

                    {/* Language */}
                    <div>
                      <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Language</h4>
                      <div className="relative">
                        <select
                          className={`w-full px-3 py-2 rounded-lg text-sm outline-none border transition-all duration-200 focus:ring-2 focus:ring-pink-500/50 focus:border-transparent ${darkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50/80 border-gray-200 text-gray-900'}`}
                        >
                          <option value="en" className={darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}>English</option>
                          <option value="es" className={darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}>Español</option>
                          <option value="fr" className={darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}>Français</option>
                          <option value="de" className={darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}>Deutsch</option>
                          <option value="it" className={darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}>Italiano</option>
                          <option value="pt" className={darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}>Português</option>
                          <option value="ru" className={darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}>Русский</option>
                          <option value="zh" className={darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}>中文</option>
                          <option value="ja" className={darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}>日本語</option>
                          <option value="ko" className={darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}>한국어</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

};

export default WaitingRoom;